import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { useChatContext, useMessageInputContext } from 'stream-chat-react';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import map from 'lodash/map';
import get from 'lodash/get';
import classNames from 'classnames';

import PlusIcon from '@ps-design-system/icons/Plus/Plus16';
import { Button } from '@ps-design-system/components/Button';
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from '@ps-design-system/components/Tooltip';
import { useToast } from '@ps-design-system/components/Toast';
import { Spinner } from '@ps-design-system/components/Spinner';

import { useLoading } from '../../../shared-components/utils/hooks/use-loading';

import {
  convertHEIF,
  formatBytes,
  getFileExtension,
  isHEICOrHEIF,
  StreamErrorIds,
  useStreamChatErrors
} from '../../utils/utils';

const AttachmentButton = ({ intl = {} }) => {
  const [settings, setSettings] = useState({});
  const [files, setFiles] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [errors, setErrors] = useState([]);

  const { toast } = useToast();

  useEffect(() => {
    if (!isEmpty(errors)) {
      forEach(errors, (error = {}) => {
        if (error.description) {
          toast({ duration: 50000, ...error })
        }
      });
      setErrors([]);
    }
  }, [errors]);

  useEffect(() => {
    if (files && 0 < files.length) {
      uploadNewFiles(files);
      setFiles([]);
    }
  }, [files]);

  const toggleTooltip = useCallback((e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setShowTooltip(prev => !prev);
  }, [showTooltip])

  const { uploadNewFiles, isUploadEnabled, maxFilesLeft } = useMessageInputContext();

  const { getAppSettings } = useChatContext();

  const { addInputErrorIds, removeInputErrorIds } = useStreamChatErrors();

  const { asyncFn: loadAppSettings, isLoading } = useLoading(async () => {
    const response = await getAppSettings();
    setSettings(response.app);
  });

  useEffect(() => {
    loadAppSettings();
  }, []);

  const inputRef = useRef(null);

  const selectFiles = () => {
    if (inputRef.current && 'undefined' !== typeof inputRef.current.click) {
      inputRef.current.click();
    }
  };

  const checkIsImage = useCallback((file = {}) => {
    const extension = getFileExtension(file);
    return includes(settings.image_upload_config.allowed_file_extensions, extension);
  }, [settings]);

  const getFileSizeLimit = useCallback((file = {}) => {
    const isImage = checkIsImage(file);

    return isImage
      ? settings.image_upload_config.size_limit
      : settings.file_upload_config.size_limit;
  }, [settings]);

  const validateSize = useCallback((file = {}) => {
    const limit = getFileSizeLimit(file);

    return file.size <= limit;
  }, [settings]);

  const validateExtension = useCallback((file = {}) => {
    const extension = getFileExtension(file);
    return includes([
      ...settings.file_upload_config.allowed_file_extensions,
      ...settings.image_upload_config.allowed_file_extensions
    ], extension);
  }, [settings]);

  const validateFiles = (files = []) => {
    const errors = {};
    const newErrors = [];
    const errorsToClear = [];

    forEach(files, (file) => {
      const isExtensionValid = validateExtension(file);
      const sizeLimit = getFileSizeLimit(file);
      const isSizeValid = validateSize(file);
      const isImage = checkIsImage(file);

      if (!isExtensionValid && !errors.extension_error) {
        errors.extension_error = true;
        newErrors.push({ id: StreamErrorIds.invalid_file_extension });
      }

      if (!isSizeValid) {
        const errorType = isImage ? 'image_size_error' : 'file_size_error';
        const errorId = isImage ? StreamErrorIds.invalid_image_size : StreamErrorIds.invalid_file_size;

        if (!errors[errorType]) {
          errors[errorType] = true;
          newErrors.push({ id: errorId, values: { size: formatBytes(sizeLimit, intl) } });
        }
      }
    });

    if (!isEmpty(newErrors)) {
      addInputErrorIds(newErrors);
    }
    if (!isEmpty(errorsToClear)) {
      removeInputErrorIds(errorsToClear);
    }

    return filter(files, (file) => {
      const isExtensionValid = validateExtension(file);
      const isSizeValid = validateSize(file);
      return isExtensionValid && isSizeValid;
    });
  };

  const { asyncFn: handleChange, isLoading: isUploading } = useLoading(async (e) => {
    if (document.activeElement && 'undefined' !== typeof document.activeElement.blur) {
      document.activeElement.blur();
    }

    try {
      const files = e.currentTarget.files;

      const results = await Promise.all(map(files, async (file) => {
        if (isHEICOrHEIF(file)) {
          try {
            const converted = await convertHEIF(file);
            const [validated] = validateFiles([converted]);
            if (validated) {
              return [validated, null];
            }
            return [null, null];
          } catch (e) {
            const error = {
              description: intl.formatMessage(
                {
                  id: 'stream.attachment_button.file_upload_error',
                  defaultMessage: 'Unable to upload {name}.'
                },
                {
                  name: file.name
                }
              ),
              variant: 'destructive'
            };
            return [null, error];
          }
        } else {
          const [validated] = validateFiles([file]);
          if (validated) {
            return [validated, null];
          }
          return [null, null];
        }
      }));

      const newErrors = filter(map(results, (result) => {
        return get(result, '1', null);
      }));

      const newFiles = filter(map(results, (result) => {
        return get(result, '0', null);
      }));

      if (files.length > maxFilesLeft) {
        newErrors.push({
          description: intl.formatMessage({
            id: 'stream.attachment_button.max_attachments_error',
            defaultMessage: 'You can only send {count} attachments at a time'
          }, { count: 10 }),
          variant: 'white'
        })
      }

      setErrors(newErrors);
      setFiles(newFiles);
      e.target.value = '';
      e.currentTarget.value = '';
    } catch (e) {}
  })

  const acceptFiles = useMemo(() => {
    if (settings.file_upload_config && settings.image_upload_config) {
      return [
        ...settings.file_upload_config.allowed_file_extensions,
        ...settings.file_upload_config.allowed_mime_types,
        ...settings.image_upload_config.allowed_file_extensions,
        ...settings.image_upload_config.allowed_mime_types
      ].join(',');
    }
    return '';
  }, [settings]);

  const isMaxFiles = 0 === maxFilesLeft;
  const isDisabled = !!isMaxFiles || isUploading;
  const isHidden = !isUploadEnabled || isLoading;

  if (isHidden) {
    return null;
  }

  return (
    <>
      <input
        className="hidden"
        type="file"
        ref={inputRef}
        accept={acceptFiles}
        onChange={handleChange}
        disabled={isDisabled}
        multiple
      />
      <Tooltip
        rootProps={{
          delayDuration: 0,
          open: showTooltip,
          onOpenChange: setShowTooltip
        }}
      >
        <TooltipTrigger onClick={toggleTooltip}>
          <Button
            type="button"
            onClick={selectFiles}
            className="StreamAttachmentButton"
            appearance="gray"
            iconOnly
            disabled={isDisabled}
          >
            {isUploading
              ? <div className="tw-flex tw-align-middle tw-justify-center tw-h-full tw-w-full"><Spinner className="attachment-spinner" appearance="black" /></div>
              : <PlusIcon />
            }
          </Button>
        </TooltipTrigger>
          <TooltipContent
            side="bottom"
            sideOffset={2}
            align="center"
            className={classNames('attachment-button-tooltip-content', { hidden: !isMaxFiles })}
          >
            <FormattedMessage
              id="stream.message_input.max_attachments_notification"
              defaultMessage="You may only add 10 attachments per message."
            />
            <TooltipArrow className="tw-fill-base-white" />
          </TooltipContent>
      </Tooltip>
    </>
  );
};

AttachmentButton.propTypes = {
  intl: intlShape
};

exports.AttachmentButton = injectIntl(AttachmentButton);
